// src/pages/StaffPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { staffAPI, warehousesAPI, companiesAPI } from '../services/api';
import { Card, Badge, SectionHeader, EmptyState, Button, Modal, Input, Select, ConfirmModal } from '../components/ui';
import toast from 'react-hot-toast';
import { Users, Plus, Pencil, Trash2, Download, Printer, Search } from 'lucide-react';
import styles from './StaffPage.module.css';

const SALARY_PERIODS = ['Monthly', 'Biweekly', 'Weekly'];
const CURRENCIES = ['HNL', 'USD', 'EUR'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Bridge print server URL — same key as ProductsPage so they share the setting
const BRIDGE_URL_KEY = 'gaafrfid_bridge_print_url';
function getBridgeUrl() { return localStorage.getItem(BRIDGE_URL_KEY) || ''; }
function saveBridgeUrl(url) { localStorage.setItem(BRIDGE_URL_KEY, url); }

export default function StaffPage() {
    const { companyId, isGaafAdmin } = useAuth();
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [badgeTarget, setBadgeTarget] = useState(null);
    const [search, setSearch] = useState('');
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');

    useEffect(() => {
        if (!isGaafAdmin) return;
        companiesAPI.list().then(res => {
            const list = res.data.companies || [];
            setCompanies(list);
            if (list.length > 0) setSelectedCompanyId(list[0].companyId);
        }).catch(() => { });
    }, [isGaafAdmin]);

    const effectiveCompanyId = isGaafAdmin ? selectedCompanyId : companyId;

    const load = useCallback(async () => {
        if (!effectiveCompanyId) return;
        setLoading(true);
        try {
            const res = await staffAPI.list({ companyId: effectiveCompanyId });
            setStaff(res.data.staff || []);
        } catch (_) { }
        setLoading(false);
    }, [effectiveCompanyId]);

    useEffect(() => { load(); }, [load]);

    const filtered = staff.filter(s =>
        !search ||
        s.fullName?.toLowerCase().includes(search.toLowerCase()) ||
        s.position?.toLowerCase().includes(search.toLowerCase()) ||
        s.department?.toLowerCase().includes(search.toLowerCase()) ||
        s.employeeId?.toLowerCase().includes(search.toLowerCase())
    );

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            await staffAPI.update(deleteTarget.staffId, { status: 'inactive' });
            toast.success('Staff Member Deactivated');
            setDeleteTarget(null);
            load();
        } catch (err) {
            toast.error('Failed to Deactivate Staff Member');
        }
        setDeleteLoading(false);
    };

    return (
        <div className={`${styles.page} animate-fadeIn`}>
            <SectionHeader
                title="Staff"
                description="Manage Staff Members and RFID Attendance Badges"
                actions={
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        {isGaafAdmin && companies.length > 0 && (
                            <select
                                value={selectedCompanyId}
                                onChange={e => setSelectedCompanyId(e.target.value)}
                                style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--text-primary)', padding: '7px 12px', fontFamily: 'var(--font-body)', fontSize: '0.875rem', outline: 'none' }}
                            >
                                {companies.map(c => <option key={c.companyId} value={c.companyId}>{c.name}</option>)}
                            </select>
                        )}
                        <Button variant="primary" icon={<Plus size={15} />} onClick={() => setShowCreate(true)}>
                            Add Staff Member
                        </Button>
                    </div>
                }
            />

            <div style={{ position: 'relative', maxWidth: 360, marginBottom: 16 }}>
                <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none' }} />
                <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search by Name, Position, Department, ID…"
                    style={{ width: '100%', paddingLeft: 34, paddingRight: 12, paddingTop: 8, paddingBottom: 8, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', color: 'var(--text-primary)', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }}
                />
            </div>

            {loading ? (
                <div className={styles.grid}>{[1, 2, 3].map(i => <div key={i} className={styles.skeleton} />)}</div>
            ) : staff.length === 0 ? (
                <EmptyState
                    icon={<Users size={40} />}
                    title={search ? 'No Staff Members match your search' : 'No Staff Members yet'}
                    description={search ? 'Try a different Name, Position, or Employee ID.' : 'Add Staff Members and Print their RFID Badges to Start Tracking Attendance Automatically.'}
                    action={!search && <Button variant="primary" icon={<Plus size={15} />} onClick={() => setShowCreate(true)}>Add First Staff Member</Button>}
                />
            ) : (
                <div className={styles.grid}>
                    {filtered.map(s => (
                        <StaffCard
                            key={s.staffId}
                            member={s}
                            onEdit={() => setEditTarget(s)}
                            onDelete={() => setDeleteTarget(s)}
                            onBadge={() => setBadgeTarget(s)}
                        />
                    ))}
                </div>
            )}

            <StaffModal
                open={showCreate || !!editTarget}
                member={editTarget}
                companyId={effectiveCompanyId}
                onClose={() => { setShowCreate(false); setEditTarget(null); }}
                onSaved={() => { setShowCreate(false); setEditTarget(null); load(); }}
            />

            <ConfirmModal
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                loading={deleteLoading}
                title="Deactivate Staff Member"
                message={`Deactivate ${deleteTarget?.fullName}? Their Attendance History is Preserved.`}
                confirmLabel="Deactivate"
            />

            <BadgeModal
                member={badgeTarget}
                open={!!badgeTarget}
                onClose={() => setBadgeTarget(null)}
            />
        </div>
    );
}

// ── Staff Card ─────────────────────────────────────────────────────────────────
function StaffCard({ member, onEdit, onDelete, onBadge }) {
    return (
        <Card className={styles.card} hover>
            <div className={styles.cardHeader}>
                <div className={styles.avatar}>
                    {(member.fullName || '?')[0].toUpperCase()}
                </div>
                <div className={styles.info}>
                    <div className={styles.name}>{member.fullName}</div>
                    <div className={styles.position}>{member.position}{member.department ? ` · ${member.department}` : ''}</div>
                    <Badge variant={member.status === 'active' ? 'success' : 'neutral'} size="sm">{member.status}</Badge>
                </div>
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    <button onClick={onEdit} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 4, borderRadius: 4, display: 'flex' }} title="Edit">
                        <Pencil size={14} />
                    </button>
                    <button onClick={onDelete} style={{ background: 'none', border: 'none', color: '#e07a7a', cursor: 'pointer', padding: 4, borderRadius: 4, display: 'flex' }} title="Deactivate">
                        <Trash2 size={14} />
                    </button>
                </div>
            </div>

            <div className={styles.details}>
                <div className={styles.detail}>
                    <span className={styles.detailLabel}>Employee ID</span>
                    <span className={styles.detailValue}>{member.employeeId}</span>
                </div>
                {member.salary && (
                    <div className={styles.detail}>
                        <span className={styles.detailLabel}>Salary</span>
                        <span className={styles.detailValue}>{member.currency} {Number(member.salary).toLocaleString()} / {member.salaryPeriod}</span>
                    </div>
                )}
                <div className={styles.detail}>
                    <span className={styles.detailLabel}>Schedule</span>
                    <span className={styles.detailValue}>
                        {member.schedule?.start || '08:00'} – {member.schedule?.end || '17:00'}
                        {' · '}{(member.schedule?.days || [1, 2, 3, 4, 5]).map(d => ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'][d]).join(',')}
                    </span>
                </div>
                <div className={styles.detail}>
                    <span className={styles.detailLabel}>Badge EPC</span>
                    <code style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>{member.badgeEpc}</code>
                </div>
            </div>

            <Button
                variant="primary"
                size="sm"
                icon={<Printer size={13} />}
                onClick={onBadge}
                fullWidth
            >
                Print Badge
            </Button>
        </Card>
    );
}

// ── Badge Modal ────────────────────────────────────────────────────────────────
function BadgeModal({ member, open, onClose }) {
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [bridgeUrl, setBridgeUrlState] = useState(getBridgeUrl);
    const [showBridgeInput, setShowBridgeInput] = useState(false);

    useEffect(() => {
        if (open && member) {
            setResult(null);
            setLoading(false);
            setPrinting(false);
            setShowBridgeInput(false);
        }
    }, [open, member?.staffId]);

    if (!member) return null;

    const handleGenerate = async () => {
        setLoading(true);
        try {
            const res = await staffAPI.encodeBadge(member.staffId);
            setResult(res.data);
        } catch (err) {
            toast.error('Failed to Generate Badge');
        }
        setLoading(false);
    };

    const downloadZPL = () => {
        if (!result?.zpl) return;
        const blob = new Blob([result.zpl], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `badge-${member.employeeId || member.staffId}-${member.fullName.replace(/ /g, '-')}.zpl`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Badge Label Downloaded');
    };

    const printBadge = async () => {
        if (!result?.zpl) return;
        const url = bridgeUrl.trim();
        if (!url) {
            setShowBridgeInput(true);
            return toast.error('Enter the Bridge PC IP address first');
        }
        setPrinting(true);
        try {
            const printUrl = url.startsWith('http') ? `${url.replace(/\/$/, '')}/print` : `http://${url}:9200/print`;
            const resp = await fetch(printUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/octet-stream' },
                body: result.zpl,
            });
            if (resp.ok) {
                toast.success('Badge sent to Printer');
            } else {
                throw new Error(`Bridge returned ${resp.status}`);
            }
        } catch (err) {
            toast.error(`Print Failed: ${err.message}. Is the bridge running?`);
            setShowBridgeInput(true);
        }
        setPrinting(false);
    };

    const handleBridgeUrl = (val) => { setBridgeUrlState(val); saveBridgeUrl(val); };

    const resetAndClose = () => { setResult(null); onClose(); };

    return (
        <Modal open={open} onClose={resetAndClose} title={`Badge — ${member.fullName}`} size="md">
            {!result ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-tertiary)' }}>
                        Generates an RFID Badge Label for <strong>{member.fullName}</strong> with EPC <code style={{ fontSize: '0.75rem' }}>{member.badgeEpc}</code>. Print it on your SATO CT4-LX printer and attach to the staff ID card.
                    </p>
                    <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem' }}>
                            <span style={{ color: 'var(--text-tertiary)' }}>Employee ID</span>
                            <span style={{ color: 'var(--text-secondary)' }}>{member.employeeId}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem' }}>
                            <span style={{ color: 'var(--text-tertiary)' }}>Position</span>
                            <span style={{ color: 'var(--text-secondary)' }}>{member.position}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem' }}>
                            <span style={{ color: 'var(--text-tertiary)' }}>Badge EPC</span>
                            <code style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{member.badgeEpc}</code>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                        <Button variant="secondary" onClick={resetAndClose}>Cancel</Button>
                        <Button variant="primary" loading={loading} icon={<Printer size={14} />} onClick={handleGenerate}>
                            Generate Badge Label
                        </Button>
                    </div>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ display: 'flex', gap: 12, background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: '14px 16px', justifyContent: 'space-around', textAlign: 'center' }}>
                        <div>
                            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)' }}>{member.fullName}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: 2 }}>Staff Member</div>
                        </div>
                        <div>
                            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 4 }}>{member.badgeEpc}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: 2 }}>Badge EPC</div>
                        </div>
                    </div>

                    {showBridgeInput && (
                        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                            <div style={{ flex: 1 }}>
                                <Input
                                    label="Bridge PC IP Address"
                                    value={bridgeUrl}
                                    onChange={e => handleBridgeUrl(e.target.value)}
                                    placeholder="192.168.0.105"
                                    hint="IP of the PC running bridge.py — port 9200 must be reachable"
                                />
                            </div>
                            <Button variant="secondary" size="sm" onClick={() => setShowBridgeInput(false)} style={{ marginBottom: 2 }}>
                                OK
                            </Button>
                        </div>
                    )}

                    <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                        {bridgeUrl
                            ? <>Print directly to the SATO printer via the bridge, or download the ZPL file.</>
                            : <>Download the ZPL file and send it to your SATO CT4-LX, or configure the bridge IP to print directly.</>
                        }
                    </p>

                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <Button variant="secondary" onClick={resetAndClose}>Done</Button>
                        {!showBridgeInput && !bridgeUrl && (
                            <Button variant="ghost" size="sm" onClick={() => setShowBridgeInput(true)}>
                                Configure Printer
                            </Button>
                        )}
                        <Button variant="secondary" icon={<Download size={14} />} onClick={downloadZPL}>
                            Download ZPL
                        </Button>
                        <Button variant="primary" icon={<Printer size={14} />} loading={printing} onClick={printBadge}>
                            Print Badge
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
}

// ── Staff Modal ────────────────────────────────────────────────────────────────
function StaffModal({ open, member, companyId, onClose, onSaved }) {
    const isEditing = !!member;
    const [warehouses, setWarehouses] = useState([]);
    const [form, setForm] = useState({
        firstName: '', lastName: '', employeeId: '', position: '', department: '',
        warehouseId: '', salary: '', salaryPeriod: 'Monthly', currency: 'HNL',
        startDate: new Date().toISOString().split('T')[0],
        scheduleStart: '08:00', scheduleEnd: '17:00',
        scheduleDays: [1, 2, 3, 4, 5],
        notes: '',
    });
    const [loading, setLoading] = useState(false);
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    useEffect(() => {
        if (!open) return;
        if (member) {
            setForm({
                firstName: member.firstName || '', lastName: member.lastName || '',
                employeeId: member.employeeId || '', position: member.position || '',
                department: member.department || '', warehouseId: member.warehouseId || '',
                salary: member.salary || '', salaryPeriod: member.salaryPeriod || 'Monthly',
                currency: member.currency || 'HNL', startDate: member.startDate || '',
                scheduleStart: member.schedule?.start || '08:00',
                scheduleEnd: member.schedule?.end || '17:00',
                scheduleDays: member.schedule?.days || [1, 2, 3, 4, 5],
                notes: member.notes || '',
            });
        } else {
            setForm({ firstName: '', lastName: '', employeeId: '', position: '', department: '', warehouseId: '', salary: '', salaryPeriod: 'Monthly', currency: 'HNL', startDate: new Date().toISOString().split('T')[0], scheduleStart: '08:00', scheduleEnd: '17:00', scheduleDays: [1, 2, 3, 4, 5], notes: '' });
        }
        setLoading(false);
        if (companyId) {
            warehousesAPI.list(companyId).then(res => {
                const list = (res.data.warehouses || []).filter(w => w.status !== 'inactive');
                setWarehouses(list);
                if (!member && list.length > 0) set('warehouseId', list[0].warehouseId);
            }).catch(() => { });
        }
    }, [open, member?.staffId, companyId]);

    const toggleDay = (day) => {
        setForm(p => ({
            ...p,
            scheduleDays: p.scheduleDays.includes(day)
                ? p.scheduleDays.filter(d => d !== day)
                : [...p.scheduleDays, day].sort(),
        }));
    };

    const handleSave = async () => {
        if (!form.firstName || !form.lastName) return toast.error('First and Last Name are Required');
        if (!form.warehouseId) return toast.error('Select a Warehouse');
        setLoading(true);
        try {
            const payload = {
                ...form,
                companyId,
                salary: form.salary ? parseFloat(form.salary) : null,
                schedule: {
                    start: form.scheduleStart,
                    end: form.scheduleEnd,
                    days: form.scheduleDays,
                },
            };
            if (isEditing) {
                await staffAPI.update(member.staffId, payload);
                toast.success('Staff Member Updated');
            } else {
                await staffAPI.create(payload);
                toast.success('Staff Member Added — Print their Badge now');
            }
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to Save Staff Member');
        }
        setLoading(false);
    };

    return (
        <Modal open={open} onClose={onClose} title={isEditing ? `Edit — ${member?.fullName}` : 'Add Staff Member'} size="lg">
            <div className={styles.formGrid}>
                <Input label="First Name *" value={form.firstName} onChange={e => set('firstName', e.target.value)} placeholder="John" />
                <Input label="Last Name *" value={form.lastName} onChange={e => set('lastName', e.target.value)} placeholder="Doe" />
                <Input label="Employee ID" value={form.employeeId} onChange={e => set('employeeId', e.target.value)} placeholder="HR-001" hint="Internal HR identifier" />
                <Input label="Position / Job Title" value={form.position} onChange={e => set('position', e.target.value)} placeholder="Warehouse Operator" />
                <Input label="Department" value={form.department} onChange={e => set('department', e.target.value)} placeholder="Operations" />
                <Select label="Warehouse *" value={form.warehouseId} onChange={e => set('warehouseId', e.target.value)}>
                    <option value="">Select Warehouse…</option>
                    {warehouses.map(w => <option key={w.warehouseId} value={w.warehouseId}>{w.name}</option>)}
                </Select>
                <Input label="Salary" type="number" min="0" step="0.01" value={form.salary} onChange={e => set('salary', e.target.value)} placeholder="15000" />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Select label="Period" value={form.salaryPeriod} onChange={e => set('salaryPeriod', e.target.value)}>
                        {SALARY_PERIODS.map(p => <option key={p} value={p}>{p}</option>)}
                    </Select>
                    <Select label="Currency" value={form.currency} onChange={e => set('currency', e.target.value)}>
                        {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </Select>
                </div>
                <Input label="Start Date" type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} />

                <div className={styles.fullSpan}>
                    <div className={styles.scheduleLabel}>Work Schedule</div>
                    <div className={styles.scheduleRow}>
                        <Input label="Check-in time" type="time" value={form.scheduleStart} onChange={e => set('scheduleStart', e.target.value)} />
                        <Input label="Check-out time" type="time" value={form.scheduleEnd} onChange={e => set('scheduleEnd', e.target.value)} />
                    </div>
                    <div className={styles.dayPicker}>
                        {[0, 1, 2, 3, 4, 5, 6].map(day => (
                            <button
                                key={day}
                                type="button"
                                className={`${styles.dayBtn} ${form.scheduleDays.includes(day) ? styles.dayBtnActive : ''}`}
                                onClick={() => toggleDay(day)}
                            >
                                {WEEKDAYS[day]}
                            </button>
                        ))}
                    </div>
                </div>

                <div className={styles.fullSpan}>
                    <Input label="Notes" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any Additional Notes…" />
                </div>

                <div className={`${styles.fullSpan} ${styles.modalActions}`}>
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" loading={loading} onClick={handleSave}>
                        {isEditing ? 'Save Changes' : 'Add Staff Member'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
